<script setup>
/**
 * FormPublishBtn
 * A toggle switch for the 'status' field (Published/Unpublished).
 * Includes permission checks and safety logic to prevent unpublishing
 * critical items like the designated Home Page.
 */
import { computed, watch, getCurrentInstance } from 'vue';

const props = defineProps({
    formId: {
        type: String,
        required: true
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- Computed ---

const formState = computed(() => store.state[props.formId]);

const currentData = computed(() => formState.value?.currentData || {});

const tableName = computed(() => formState.value?.tableName);

const id = computed(() => formState.value?.id);

const formSmall = computed(() => formState.value?.formSmall);

const statusLabel = computed(() => currentData.value.status ? 'Published' : 'Unpublished');

const component = computed(() => {
    return store.state.components.items.find(obj => obj.tableName === tableName.value);
});

const hasPublishAccess = computed(() => {
    return store.getters['user/access'](tableName.value, 'publish');
});

const show = computed(() => {
    return hasPublishAccess.value && currentData.value.status !== undefined;
});

const allowPublish = computed(() => {
    // Safety check: Prevent unpublishing the site's home page
    if (tableName.value === 'pages') {
        return store.state.settings.homePage !== id.value;
    }
    return true;
});

// --- Watchers ---

watch(() => props.formId, () => {
    if (component.value?.showStatus && id.value === 0) {
        store.dispatch(`${props.formId}/setDefaultValue`, {
            name: 'status',
            value: 0
        });
    }
}, { immediate: true });

// --- Methods ---

const onClick = () => {
    if (allowPublish.value) {
        currentData.value.status = !currentData.value.status ? 1 : 0;
    }
};
</script>

<template>
    <button
        v-if="show"
        :class="[
            currentData.status ? 'btn-success' : 'btn-danger',
            {'btn-sm': formSmall}
        ]"
        :disabled="!allowPublish"
        class="btn wizard-form-control-publish"
        type="button"
        @click="onClick"
    >
        <span>{{ statusLabel }}</span>
    </button>
</template>